import {Component, HostListener, inject} from '@angular/core';
import {TranslationService} from "../../core/services/translation.service";
import {FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators} from "@angular/forms";
import {IdentificationService} from "../../core/services/identification.service";
import {IdentificationData} from "../../data/models/identification-data.model";
import {Person} from "../../data/models/person.model";
import {Address} from "../../data/models/address.model";
import {LoginService} from "../../core/components/login/login.service";
import {NavigationCommand, NavigationService} from "../../data/services/navigation.service";
import {CustomValidators} from "../../core/validators";
import { HttpClient } from "@angular/common/http";
import {ConfigService} from "../../core/services/config.service";
import {ActivatedRoute, Router} from "@angular/router";

import {LoadingOverlayComponent} from "../../core/components/loading-overlay/loading-overlay.component";
import {GeneralErrorsComponent} from "../../core/components/general-errors/general-errors.component";
import {
  HupSubscriptionAddressComponent
} from "../hup-subscription/hup-subscription-detail/hup-subscription-address/hup-subscription-address.component";
import {MatButton} from "@angular/material/button";
import {MatError, MatFormField, MatLabel} from "@angular/material/form-field";
import {MatInput} from "@angular/material/input";
import {MatCheckbox} from "@angular/material/checkbox";
import {MatProgressSpinner} from "@angular/material/progress-spinner";
import {MatIcon} from "@angular/material/icon";
import {MatCard, MatCardContent} from "@angular/material/card";
import {ApplyIfNotEmptyDirective} from "../../core/directives/apply-if-not-empty.directive";
import {CustomerType} from "../../core/models/customer-type";
import {MatRadioModule} from "@angular/material/radio";

@Component({
  selector: 'app-identification',
  templateUrl: './identification.component.html',
  styleUrls: ['./identification.component.css'],
  imports: [
    LoadingOverlayComponent,
    GeneralErrorsComponent,
    HupSubscriptionAddressComponent,
    MatButton,
    MatFormField,
    MatInput,
    ReactiveFormsModule,
    MatCheckbox,
    MatProgressSpinner,
    MatIcon,
    MatError,
    MatLabel,
    MatCard,
    MatCardContent,
    ApplyIfNotEmptyDirective,
    MatRadioModule,
    FormsModule
  ],
  standalone: true
})
export class IdentificationComponent{
  translationService = inject(TranslationService);
  private formBuilder = inject(FormBuilder);
  private route = inject(ActivatedRoute);
  identificationService = inject(IdentificationService);
  private loginService = inject(LoginService);
  private navigationService = inject(NavigationService);
  private http = inject(HttpClient);
  private configService = inject(ConfigService);
  private router = inject(Router);

  isLoading: boolean = false;
  errorsArr: string[] = [];
  formGroup: FormGroup;
  showIBAN: boolean = false;
  showPhone: boolean = false;
  showBirthday: boolean = false;
  showEmail: boolean = false;
  showClientNo: boolean = false;
  identificationData: IdentificationData = null;
  notFoundAddress: boolean = true;
  notFoundExtendedData: boolean = true;
  searched: boolean = false;
  maskedOverviewText: string;
  returnPath: NavigationCommand;
  registeringAgreed: boolean = false;
  /** Route erzwingt die Registrierung: "ohne Registrierung fortfahren" wird nicht angeboten. */
  forceRegistration: boolean = false;
  customerType: CustomerType = CustomerType.PRIVATE;
  /** Suchschlüssel der zuletzt abgeschickten Suche; siehe {@link buildSearchKey}. */
  private lastSearchKey: string = null;

  constructor() {
    const translationService = this.translationService;


    this.maskedOverviewText = '';
    this.formGroup = this.formBuilder.group({
      searchAddress: this.formBuilder.group({
        showAddress: [''],
        address: this.formBuilder.group({
          country: [''],
          zipcode: [''],
          city: [''],
          street: [''],
          houseNo: [''],
          extraLine: [''],
          houseNoExt: [''],
          stiege: [''],
          stock: [''],
          tuer: [''],
          district: [''],
        }),
        person: this.formBuilder.group({
          title: [''],
          salutation: [''],
          firstName: [''],
          lastName: [''],
          initials: [''],
          middlename: [''],
        }),
        extendedValues: this.formBuilder.group( {
          email: ['',[CustomValidators.checkIdentificationFieldIsCorrect( 'EMAIL', () => this.identificationData)]],
          phone: ['',[CustomValidators.checkIdentificationFieldIsCorrect( 'PHONE', () => this.identificationData)]],
          iban: ['',[CustomValidators.checkIdentificationFieldIsCorrect( 'IBAN', () => this.identificationData)]],
          birthday: ['',[CustomValidators.checkIdentificationFieldIsCorrect( 'BIRTHDAY', () => this.identificationData)]],
          clientNo: ['',[CustomValidators.checkIdentificationFieldIsCorrect( 'CLIENTNO', () => this.identificationData)]],
          copyEmail: [false],
          registerEmail: ['', [Validators.required, Validators.pattern('^(([^<>()[\\]\\\\.,;:\\s@"]+(\\.[^<>()[\\]\\\\.,;:\\s@"]+)*)|.(".+"))@((\\[[0-9]{1,3}\\.[0-9]{1,3}\\.[0-9]{1,3}\\.[0-9]{1,3}\\])|(([a-zA-Z\\-0-9]+\\.)+[a-zA-Z]{2,}))$')]]
        })
      })
    });

    this.route.paramMap.subscribe((params) => {
      const returnPath = params.get('returnPath');
      if(returnPath) {
        this.returnPath = returnPath as NavigationCommand;
      }
    });

    this.route.data.subscribe(data => {
      this.forceRegistration = !!data?.forceRegistration;
    });

    this.formGroup.get('searchAddress.address').valueChanges.subscribe( () => {
      this.resetIfSearchChanged();
    });

    this.formGroup.get('searchAddress.person').valueChanges.subscribe( () => {
      this.resetIfSearchChanged();
    });

    this.formGroup.get('searchAddress.extendedValues.copyEmail').valueChanges.subscribe( value => {
      if (value) {
        this.formGroup.get('searchAddress.extendedValues.registerEmail').setValue(this.formGroup.get('searchAddress.extendedValues.email').value);
      } else {
        this.formGroup.get('searchAddress.extendedValues.registerEmail').setValue('');
      }
    });

    this.loginService.getAuthData().subscribe(userData => {
      if (userData && userData.token) {
        // User wurde eingeloggt, also redirect zum übergebenen returnPath oder /subscription
        this.navigationService.navigateTo(this.returnPath? this.returnPath : NavigationCommand.SUBSCRIPTION);
      }
    });

    this.formGroup.get('searchAddress.extendedValues.registerEmail').setAsyncValidators(CustomValidators.checkEmailAddress(
      this.http,
      this.translationService,
    ));

    const matchCount = this.configService.getConfig('identification.matchCount');
    if (matchCount && matchCount.value && matchCount.value !== '') {
      this.maskedOverviewText = translationService.getTranslation('identification_masked_overview');
      if (this.maskedOverviewText.includes('{count')) {
        this.maskedOverviewText = this.maskedOverviewText.replace('{count}', matchCount.value);
      }
    }
  }

  /**
   * Löst per Enter die Aktion des aktiven Schritts aus — analog zu dem Button, der im jeweiligen
   * Schritt sichtbar ist:
   *
   * - noch kein Suchergebnis: die Adress-Suche ({@link loadAddress}),
   * - Suchergebnis ohne verifizierten Nutzer: die Prüfung der maskierten Daten
   *   ({@link checkMaskedData}),
   * - verifizierter Nutzer: nichts — dort ruft das Register-E-Mail-Feld createAccount() selbst
   *   über (keyup.enter) auf.
   *
   * Ohne diese Unterscheidung suchte Enter in jedem Feld erneut, verwarf über
   * {@link resetVariables} das Suchergebnis und warf den bereits verifizierten Nutzer zurück
   * ins Suchformular.
   */
  @HostListener('document:keydown.enter', ['$event'])
  handleEnterKey(event: KeyboardEvent): void {
    if (this.isLoading) {
      return;
    }
    if (!this.identificationData) {
      if (this.formGroup.get('searchAddress').get('person').valid && this.formGroup.get('searchAddress').get('address').valid) {
        event.preventDefault();
        this.loadAddress();
      }
      return;
    }
    if (this.identificationData.maskedExtendedData?.size > 0 && !this.identificationData.user) {
      event.preventDefault();
      this.checkMaskedData();
    }
  }

  public loadAddress(): void {
    this.isLoading = true;
    this.searched = true;
    const identificationData: IdentificationData = this.buildIdentificationModelOfFormGroup();
    this.resetVariables();
    this.lastSearchKey = this.buildSearchKey();
    this.identificationService.checkUserAndGetMaskedExtendedData(identificationData).subscribe(result => {
      this.isLoading = false;
      if (!result) {
        this.notFoundAddress = true;
      } else {
        // Der User wurde gefunden und es sind extendedDatas vorhanden!
        if (result.maskedExtendedData && result.maskedExtendedData.size > 0) {
          this.identificationData = result;
          // Felder in der UI anzeigen und vollständig vom User abfragen.
          result.maskedExtendedData.forEach((value, key) => {
            switch (key) {
              case 'EMAIL':
                this.showEmail = true;
                break;
              case 'PHONE':
                this.showPhone = true;
                break;
              case 'IBAN':
                this.showIBAN = true;
                break;
              case 'BIRTHDAY':
                this.showBirthday = true;
                break;
              case 'CLIENTNO':
                this.showClientNo = true;
                break;
            }
          });
        } else {
          this.notFoundExtendedData = true;
        }
      }
    }, error => {
      this.isLoading = false;
    });
  }

  public checkMaskedData(): void {
    this.isLoading = true;
    const identificationData: IdentificationData = this.buildIdentificationModelOfFormGroup();
    this.identificationService.checkUserAndValidateExtendedData(identificationData).subscribe(result => {
      this.isLoading = false;
      if (result) {
        this.identificationData = result;
        this.applyMatchResult('email', this.showEmail);
        this.applyMatchResult('phone', this.showPhone);
        this.applyMatchResult('iban', this.showIBAN);
        this.applyMatchResult('birthday', this.showBirthday);
        this.applyMatchResult('clientNo', this.showClientNo);
        if (this.showEmail && result.matchEmail) {
          this.formGroup.get('searchAddress.extendedValues.registerEmail').setValue(this.formGroup.get('searchAddress.extendedValues.email').value);
        }
      }
    }, error => {
      this.isLoading = false;
    });
  }

  /**
   * Übernimmt das Prüfergebnis eines Merkmals ins Formular, indem das Feld neu validiert wird.
   *
   * Das Feld wird zuvor als berührt markiert: sowohl
   * {@link CustomValidators.checkIdentificationFieldIsCorrect} als auch die Fehleranzeige von
   * Angular Material greifen nur bei touched. Wer den Wert eintippt und die Prüfung mit Enter
   * auslöst, verlässt das Feld nie — ohne markAsTouched() blieb die Meldung zum falschen Merkmal
   * deshalb aus. Bei einem Treffer räumt dieselbe Revalidierung eine alte Meldung wieder ab.
   */
  private applyMatchResult(controlName: string, shown: boolean): void {
    if (!shown) {
      return;
    }
    const control = this.formGroup.get(`searchAddress.extendedValues.${controlName}`);
    control.markAsTouched();
    control.updateValueAndValidity();
  }

  public continueWithoutRegistration(): void {
    if (this.forceRegistration) {
      return;
    }
    this.isLoading = true;
    if(this.identificationData?.user && this.returnPath?.length > 0) {
      this.returnPath = this.returnPath + '-without-login' as NavigationCommand;
      this.navigationService.navigateTo(this.returnPath, [this.identificationData.user.userBackendId.toString()]);
    }
  }

  public createAccount(): void {
    if (this.formGroup.get('searchAddress.extendedValues').invalid) {
      return;
    }

    this.isLoading = true;
    const identificationData: IdentificationData = this.buildIdentificationModelOfFormGroup();
    this.editIdentificationData(identificationData);

    this.identificationService.createAccount(identificationData).subscribe(result => {
      if (result && result.user && result.otlc && result.otlc !== '') {
        // alles ist gut, der User wurde zurückgegeben
        // Dann müssen wir den User nun mit dem OTLC einloggen
        this.loginService.loginByOtlc(result.otlc);
        if (this.forceRegistration && this.returnPath) {
          this.router.navigate([this.returnPath], {state: {justRegistered: true}});
        }
      } else {
        this.isLoading = false;
      }
    }, error => {
      this.isLoading = false;
    });
  }

  private editIdentificationData(identificationData: IdentificationData) {
    identificationData.matchClientNo = this.identificationData?.matchClientNo;
    identificationData.matchBirthday = this.identificationData?.matchBirthday;
    identificationData.matchEmail = this.identificationData?.matchEmail;
    identificationData.matchIBAN = this.identificationData?.matchIBAN;
    identificationData.matchPhone = this.identificationData?.matchPhone;
  }

  public goToLogin(): void {
    if(this.identificationData?.user) {
      const username: string = this.formGroup.get('searchAddress.extendedValues.registerEmail').value;
      this.router.navigate(
        ['/login'],
        {
          queryParams: {
            returnUrl: this.returnPath,
            prefillUsername: username,
            clientNo: this.identificationData.user.userBackendId.toString()
          }
        }
      )
    } else {
      this.navigationService.navigateTo(NavigationCommand.LOGIN);
    }
  }

  /**
   * Verwirft das Suchergebnis nur, wenn der Nutzer die Suche tatsächlich geändert hat.
   *
   * Auf dem Formularbaum entstehen valueChanges auch ohne Wertänderung — z.B. weil
   * {@link HupSubscriptionAddressComponent} beim Neuaufbau enable() aufruft oder ein abgeleitetes
   * Feld beschrieben wird. Ohne diesen Vergleich hat ein solches Ereignis das gerade geladene
   * Ergebnis gelöscht und den Nutzer zurück ins Suchformular geworfen (P5W-4383).
   */
  private resetIfSearchChanged(): void {
    if (this.buildSearchKey() !== this.lastSearchKey) {
      this.resetVariables();
    }
  }

  /**
   * Fasst genau die Felder zusammen, die {@link buildIdentificationModelOfFormGroup} an die Suche
   * schickt. Abgeleitete Felder wie district gehören bewusst nicht dazu — sie ändern die Suche nicht.
   */
  private buildSearchKey(): string {
    return JSON.stringify([
      this.formGroup.get('searchAddress.person.firstName').value,
      this.formGroup.get('searchAddress.person.lastName').value,
      this.formGroup.get('searchAddress.address.street').value,
      this.formGroup.get('searchAddress.address.houseNo').value,
      this.formGroup.get('searchAddress.address.zipcode').value,
      this.formGroup.get('searchAddress.address.city').value,
      this.formGroup.get('searchAddress.address.country').value?.key,
    ]);
  }

  private resetVariables(): void {
    this.showEmail = false;
    this.showPhone = false;
    this.showIBAN = false;
    this.showBirthday = false;
    this.showClientNo = false;
    this.identificationData = null;
    this.notFoundAddress = false;
    this.notFoundExtendedData = false;
  }

  private buildIdentificationModelOfFormGroup(): IdentificationData {
    const person = new Person(null ,null,
      this.formGroup.get('searchAddress.person.firstName').value,
      this.formGroup.get('searchAddress.person.lastName').value,
      null, null);
    const address = new Address(
      this.formGroup.get('searchAddress.address.street').value,
      this.formGroup.get('searchAddress.address.houseNo').value,
      this.formGroup.get('searchAddress.address.zipcode').value,
      this.formGroup.get('searchAddress.address.city').value,
      this.formGroup.get('searchAddress.address.country').value?.key,
      null, null, null, null, null, null, null);
    const extendedData = new Map<string, string>();
    if (this.formGroup.get('searchAddress.extendedValues.email').value) {
      extendedData.set('EMAIL', this.formGroup.get('searchAddress.extendedValues.email').value);
    }
    if (this.formGroup.get('searchAddress.extendedValues.iban').value) {
      extendedData.set('IBAN', this.formGroup.get('searchAddress.extendedValues.iban').value);
    }
    if (this.formGroup.get('searchAddress.extendedValues.birthday').value) {
      extendedData.set('BIRTHDAY', this.formGroup.get('searchAddress.extendedValues.birthday').value);
    }
    if (this.formGroup.get('searchAddress.extendedValues.phone').value) {
      extendedData.set('PHONE', this.formGroup.get('searchAddress.extendedValues.phone').value);
    }
    if (this.formGroup.get('searchAddress.extendedValues.clientNo').value) {
      extendedData.set('CLIENTNO', this.formGroup.get('searchAddress.extendedValues.clientNo').value);
    }
    return {
      person,
      address,
      extendedData,
      maskedExtendedData: this.identificationData?.maskedExtendedData ?? new Map(),
      matchIBAN: false,
      matchPhone: false,
      matchBirthday: false,
      matchEmail: false,
      matchClientNo: false,
      user: this.identificationData?.user ?? null,
      otlc: null,
      registerEmail: this.formGroup.get('searchAddress.extendedValues.registerEmail').value,
      enoughMatches: false};
  }

  protected readonly CustomerType = CustomerType;
}
